import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Hint,
  SelectorChip,
  Skeleton,
} from '@rammetec/design-system'
import { Building2, Check } from 'lucide-react'
import { useCurrentUser, useOrgLabels, useSessionContext } from '@vectorhub/application'

/** Org-only selector — VH has no profiles (D0009). */
export function OrgSelector() {
  const { user } = useCurrentUser()
  const { currentOrgId, setCurrentOrgId, availableOrgIds, isLoading } = useSessionContext()
  const orgLabels = useOrgLabels(availableOrgIds)

  if (!user) return null

  if (isLoading) {
    return <Skeleton className="hidden h-8 w-24 rounded-full sm:block" />
  }

  if (availableOrgIds.length === 0) return null

  const current = currentOrgId ? orgLabels.get(currentOrgId) : undefined

  // Chip: abbreviation when available; otherwise shorten the name/ID.
  const chipLabel = current?.abbreviation
    || (current?.name && current.name !== currentOrgId
        ? current.name
        : (currentOrgId?.slice(0, 4).toUpperCase() ?? '...'))

  return (
    <DropdownMenu>
      <Hint
        label={current?.name ? `Organização: ${current.name}` : 'Selecionar organização'}
        shortcut="Ctrl+Alt+E"
        side="bottom"
      >
        <DropdownMenuTrigger asChild>
          <SelectorChip
            icon={<Building2 />}
            label={chipLabel}
            disabled={availableOrgIds.length <= 1}
          />
        </DropdownMenuTrigger>
      </Hint>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="flex items-center gap-2 text-xs text-fg-muted">
          <Building2 className="h-3.5 w-3.5" />
          Organizações
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {availableOrgIds.map((orgId) => {
          const org = orgLabels.get(orgId)
          const isActive = orgId === currentOrgId
          return (
            <DropdownMenuItem
              key={orgId}
              onSelect={() => setCurrentOrgId(orgId)}
              disabled={isActive}
              className="gap-2"
            >
              {isActive ? (
                <Check className="h-4 w-4 text-accent" />
              ) : (
                <Building2 className="h-4 w-4 text-fg-muted" />
              )}
              <span className="flex min-w-0 flex-col">
                <span className="truncate">{org?.name ?? orgId}</span>
                {org?.abbreviation && org.abbreviation !== org.name ? (
                  <span className="truncate text-xs text-fg-muted">{org.abbreviation}</span>
                ) : null}
              </span>
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
